import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import {
  CheckRounded as CheckIcon,
  CloseRounded as CloseIcon,
  ContentCopyRounded as ContentCopyIcon,
  IosShareRounded as IosShareIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { api, SuperShowcase } from '../../services/api';
import { copyText } from '../share-links/shareLinkFormat';

export type ShowcaseShareMethod = 'copy' | 'whatsapp' | 'native';

interface ShowcaseShareDialogProps {
  /** The showcase being shared; null keeps the dialog closed. */
  showcase: SuperShowcase | null;
  onClose: () => void;
  onShared?: (showcase: SuperShowcase, method: ShowcaseShareMethod) => void;
}

function shareMessage(title: string, url: string): string {
  return `Check out "${title}" on Super Showcase:\n${url}`;
}

/**
 * Share sheet for one showcase. The link opens Super Picker straight on the
 * showcase; it is shortened server-side on the first share and reused after.
 */
const ShowcaseShareDialog: React.FC<ShowcaseShareDialogProps> = ({ showcase, onClose, onShared }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const showcaseId = showcase?.id ?? null;
  const storedShortUrl = showcase?.shortUrl ?? null;

  useEffect(() => {
    setCopied(false);
    setError(null);
    if (showcaseId == null) {
      setUrl(null);
      return;
    }
    if (storedShortUrl) {
      setUrl(storedShortUrl);
      return;
    }
    setUrl(null);
    let cancelled = false;
    api.getSuperShowcaseShareLink(showcaseId)
      .then((result) => { if (!cancelled) setUrl(result.url || result.fullUrl); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to create the share link'); });
    return () => { cancelled = true; };
  }, [showcaseId, storedShortUrl]);

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleCopy = async () => {
    if (!showcase || !url) return;
    if (await copyText(url)) {
      setCopied(true);
      onShared?.(showcase, 'copy');
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (!showcase || !url) return;
    try {
      await navigator.share({ title: showcase.title, text: shareMessage(showcase.title, url) });
      onShared?.(showcase, 'native');
    } catch {
      // Dismissed by the user — nothing to do.
    }
  };

  return (
    <Dialog
      open={showcase != null}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', borderRadius: 3, overflow: 'hidden' } }}
    >
      {showcase && (
        <Box
          component="img"
          src={showcase.imageUrl}
          alt=""
          sx={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }}
        />
      )}
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="overline" sx={{ color: 'var(--color-accent)', fontWeight: 700, letterSpacing: '0.08em' }}>
          Share showcase
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.25 }}>
          {showcase?.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.5 }}>
          The link opens this showcase straight in Super Picker.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : (
          <Box
            sx={{
              mt: 2,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: '1px dashed var(--color-accent)',
              bgcolor: 'var(--color-bg)',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              color: 'var(--color-text-strong)',
              wordBreak: 'break-all',
              minHeight: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {url ?? (
              <>
                <CircularProgress size={16} sx={{ color: 'var(--color-accent)' }} />
                <Box component="span" sx={{ fontFamily: 'inherit', color: 'var(--color-text-muted)' }}>Creating link…</Box>
              </>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={!url}
            onClick={handleCopy}
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            sx={{
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              fontWeight: 700,
              '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.1)' },
            }}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!url}
              startIcon={<WhatsAppIcon />}
              href={url && showcase ? `https://wa.me/?text=${encodeURIComponent(shareMessage(showcase.title, url))}` : '#'}
              target="_blank"
              rel="noopener"
              onClick={() => { if (showcase) onShared?.(showcase, 'whatsapp'); }}
              sx={{ bgcolor: '#25D366', color: '#fff', fontWeight: 700, '&:hover': { bgcolor: '#1EBE5A' } }}
            >
              WhatsApp
            </Button>
            {canNativeShare && (
              <Button
                fullWidth
                variant="outlined"
                disabled={!url}
                startIcon={<IosShareIcon />}
                onClick={handleNativeShare}
                sx={{ fontWeight: 700, color: 'var(--color-text-strong)', borderColor: 'var(--color-text-muted)' }}
              >
                More…
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShowcaseShareDialog;
